import store from '@/store';
import { globalConfig } from '@/utils';

/**
 * Feature flags **de configuration**, portés par `configuration_config_param`
 * avec `module = 'FEATURE_FLAG'` et chargés au login dans
 * `authModule.configuration.feature_flag`.
 *
 * À ne pas confondre avec `@/utils/user-feature-flags`, qui traite les flags
 * **par utilisateur** — ceux-là passent par les groupes de sécurité, pas par la
 * configuration.
 */
export const CONFIGURATION_FEATURE_FLAGS = {
  /** Versement des documents dans le coffre-fort MiSIV. */
  MISIV: 'MISIV',
  /** Versement des documents dans le coffre-fort Cecurity. */
  CECURITY: 'CECURITY',
  /** Boîtes mail entrantes CoreDI (Gmail / Office 365) paramétrables depuis la console. */
  MAIL_INBOUND: 'MAIL_INBOUND',
} as const;

/** Une entrée de `feature_flag`, telle que rendue par la requête de configuration. */
interface FeatureFlagEntry {
  field?: string;
  /** Alias historique de la requête `configuration-superset` — la faute de frappe est dans le contrat. */
  fiedld_value?: string;
}

/**
 * Liste des flags de configuration, avec repli sur le stockage local.
 *
 * Le repli n'est pas une précaution de style : au rechargement d'une page profonde,
 * le store peut ne pas être encore réhydraté alors que le stockage local, lui, est
 * déjà disponible. Sans ce repli, un flag actif serait vu comme inactif le temps
 * de l'hydratation — et l'affordance disparaîtrait puis réapparaîtrait.
 *
 * @returns les entrées de flags, éventuellement vide
 */
function readFeatureFlags(): FeatureFlagEntry[] {
  const fromStore = store.state.authModule?.configuration?.feature_flag;
  if (Array.isArray(fromStore) && fromStore.length > 0) {
    return fromStore;
  }

  const raw = localStorage.getItem(globalConfig.storageKeys.configuration);
  if (!raw || raw === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(raw)?.feature_flag;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Stockage local corrompu : on répond « aucun flag » plutôt que de laisser
    // remonter une exception dans un getter de composant.
    return [];
  }
}

/**
 * Indique si un flag de configuration est activé.
 *
 * Un flag absent vaut **désactivé** : c'est ce qui permet de livrer une
 * fonctionnalité sans l'exposer tant que le paramétrage n'a pas été posé.
 *
 * @param flag nom du flag, tel qu'il figure dans le champ `field`
 * @returns `true` uniquement si la valeur vaut littéralement `'true'`
 */
export function isConfigurationFeatureEnabled(flag: string): boolean {
  const entry = readFeatureFlags().find((item) => item?.field === flag);
  return (entry?.fiedld_value ?? '').trim().toLowerCase() === 'true';
}

/**
 * Entrée brute d'un paramètre FEATURE_FLAG (null si absent), pour lire une valeur
 * qui n'est pas un booléen.
 */
export function getConfigurationFeatureEntry(flag: string): any {
  return readFeatureFlags().find((item) => item?.field === flag) ?? null;
}

/**
 * Le statut de commande permet-il de planifier une livraison ?
 * Statuts autorisés : paramètre DELIVERY_APPOINTMENT (Configuration > Général), séparés par des virgules.
 * Paramètre absent ou vide : aucun statut autorisé.
 */
export function isDeliveryAppointmentStatusAllowed(status: string): boolean {
  const valeur = getConfigurationFeatureEntry('DELIVERY_APPOINTMENT')?.fiedld_value || '';
  const autorises = valeur.split(',').map((s: string) => s.trim()).filter(Boolean);
  return autorises.includes(status);
}

/**
 * Raccourci pour le flag MiSIV.
 *
 * @returns `true` si le versement au coffre-fort MiSIV est activé
 */
export function isMisivFeatureEnabled(): boolean {
  return isConfigurationFeatureEnabled(CONFIGURATION_FEATURE_FLAGS.MISIV);
}

/**
 * Raccourci pour le flag des boîtes mail entrantes.
 *
 * @returns `true` si l'écran et l'API de paramétrage des boîtes mail sont ouverts
 */
export function isMailInboundFeatureEnabled(): boolean {
  return isConfigurationFeatureEnabled(CONFIGURATION_FEATURE_FLAGS.MAIL_INBOUND);
}

/**
 * Indique si la synchronisation manuelle du coffre-fort est exposée.
 *
 * La CTA est unique et neutre : c'est le serveur qui tranche le coffre de destination,
 * à partir du paramétrage réseau. Le front n'a donc pas à distinguer les connecteurs,
 * seulement à savoir si la fonction est ouverte.
 *
 * L'un ou l'autre flag suffit, et c'est délibéré : un environnement déjà paramétré sur
 * `MISIV` conserve son comportement sans intervention, tandis qu'un réseau Cecurity
 * s'active en posant `CECURITY`. Un flag unique aurait fait disparaître le bouton
 * partout où il fonctionnait, le temps que le paramétrage suive.
 *
 * @returns `true` si au moins un connecteur de coffre est activé
 */
export function isVaultSyncFeatureEnabled(): boolean {
  return isConfigurationFeatureEnabled(CONFIGURATION_FEATURE_FLAGS.MISIV)
    || isConfigurationFeatureEnabled(CONFIGURATION_FEATURE_FLAGS.CECURITY);
}
